/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// exportForecast.js — branded native-.xlsx export of the on-screen forecast.
//
// Generates a real OOXML workbook with ExcelJS (so it opens in Excel with NO
// "format/extension mismatch" warning), containing:
//   • the company / team logo (the tenant's uploaded logo if set, else the
//     Brand Command logo) embedded as an image,
//   • the export date, the user who ran it, the (bold) forecast year(s), and
//     the active filters,
//   • a Brand-Command-styled grid grouped by Bottle Size / Pack Size, with
//     per-year month columns + (bold) year totals — and an all-years column
//     when more than one year is shown,
//   • multi-tier totals: per-SKU rows, a Bottle Size / Pack Size subtotal block
//     at the bottom (fenced by accent lines), and a grand total,
//   • OPTIONALLY a second "Forecast Chart" worksheet with a chart image,
//     grouped by a user-chosen dimension.
//
// Numbers come from buildForecastView() / buildChartSeries() — the SAME
// aggregation the grid + chart render — so the spreadsheet matches the screen.

// ExcelJS is heavy (~950 kB); load it on demand (inside exportGridToExcel) so it
// stays out of the main bundle and only downloads when a user actually exports.
import { buildForecastView, buildChartSeries, MONTHS, MONTH_ABBR } from "./forecastAggregate.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

// Backlog 330: an export with no usable tenant logo goes out UNBRANDED — we
// never substitute the Brand Command mark into a document the customer emails
// to a distributor or a buyer. Say so in the export dialog instead.
export const LOGO_NOTICE = {
  none: "Exported without a logo — your company hasn't uploaded one yet. Add it in System Settings so your forecasts carry your brand.",
  failed: "Exported without a logo — your company logo could not be loaded. Everything else in the workbook is complete.",
};

// ARGB palette (Excel wants AARRGGBB).
// ── ORANGE: follows the app (backlog 145/166). A spreadsheet is ALWAYS a light
//    surface, so these are the LIGHT-theme values from styles.css, and the two
//    roles are kept apart for the reason that block spells out:
//      accent     #8F5408  TEXT on white — 4.57:1. The fill orange is 1.96:1
//                          here, i.e. invisible, so text must NOT use it.
//      accentBar  #F2A33C  solid divider bars — no text sits on them.
//    Was #E85D26 (the retired brand orange), which made a downloaded sheet a
//    different orange from the app that produced it.
const C = {
  navy: "FF1B2A4A",
  band: "FF2E4A72",
  accent: "FF8F5408",
  accentBar: "FFF2A33C",
  groupBg: "FFEEF2F8",
  subBg: "FFF6F8FC",
  border: "FFD4DCEA",
  white: "FFFFFFFF",
  text: "FF1B2A4A",
  muted: "FF6B7A99",
  altRow: "FFFBFCFE",
  totalBg: "FFF1F5FB",
};

// Hex palette for the chart series (leads with the app orange).
// ⚠️ Must stay byte-identical to `ForecastChart.jsx` COLORS and to
// `depletions-inventory/utils/chartImage.js` CHART_COLORS — when these three
// drifted, the on-screen chart and its own export disagreed about which SKU was
// orange (backlog 166). Duplicated rather than shared so each module stays
// liftable (MODULES.md rule 5).
const CHART_COLORS = [
  "#F2A33C", "#3b82f6", "#4eca8d", "#a78bfa", "#22d3ee",
  "#84cc16", "#f472b6", "#facc15", "#60a5fa", "#34d399",
  "#f87171", "#c084fc", "#fbbf24", "#fb923c", "#2dd4bf",
];

// LINE MODE ONLY — backlog 323. A 2.5px stroke is a "graphical object required
// to understand the content" under WCAG 1.4.11, which wants 3:1 against the
// ground it sits on. On the white export sheet CHART_COLORS measures 1.53:1
// (#facc15) to 3.68:1 (#3b82f6) — 14 of the 15 fail, the lead orange #F2A33C at
// 2.08:1. The item logged only the orange because only the orange was measured;
// re-measuring the rest found the same defect in all but one.
//
// These are the SAME hues and saturations, with only HSL lightness squeezed
// into the band that clears 3:1 on both grounds a line is ever drawn on
// (relative order preserved, so the palette still reads as the same ramp):
//   vs #FFFFFF (export sheet + light theme canvas): 3.19–4.11:1
//   vs #1B2A4A (dark theme chart canvas):           3.46–4.45:1
// Filled BARS keep CHART_COLORS untouched — a stacked bar is a large fill, is
// not thin-line-thin, and already reads at any of these values.
//
// ⚠️ Same rule as CHART_COLORS: must stay byte-identical to LINE_COLORS in
// `ForecastChart.jsx` and `depletions-inventory/utils/chartImage.js`, so the
// exported chart is the same colours as the app that produced it (backlog 166).
const LINE_COLORS = [
  "#C5750D", "#2C78F5", "#2E9A65", "#8D69F9", "#0D99AF",
  "#629810", "#EF3194", "#AF8C04", "#2282F8", "#229C70",
  "#F53636", "#AA59FB", "#B78503", "#D96404", "#209B8C",
];

const THIN = { style: "thin", color: { argb: C.border } };
const ALL_BORDERS = { top: THIN, left: THIN, bottom: THIN, right: THIN };

function esc(v) {
  return String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

// ── Image helpers ────────────────────────────────────────────────────────────

function svgToPngDataUrl(svg, w, h) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL("image/png")); } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
    } catch (e) { reject(e); }
  });
}

function fetchAsDataUrl(url) {
  return fetch(url, { mode: "cors" })
    .then((r) => { if (!r.ok) throw new Error(`logo ${r.status}`); return r.blob(); })
    .then((blob) => new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    }));
}

// Rasterize ANY logo (svg / png / jpg / webp data-URL) to a PNG sized to fit
// maxW x maxH. ExcelJS + Excel reliably render PNG; embedding the original bytes
// (e.g. an SVG or webp, or some PNGs) is what caused "this picture can't be
// displayed". Returns { base64, width, height } or null.
function rasterizeToPng(dataUrl, maxW, maxH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth || maxW, nh = img.naturalHeight || maxH;
      const scale = Math.min(maxW / nw, maxH / nh, 1);
      const width = Math.max(1, Math.round(nw * scale));
      const height = Math.max(1, Math.round(nh * scale));
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve({ base64: canvas.toDataURL("image/png").split(",")[1], width, height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// The tenant's logo as a data-URI, or null. Never throws, and never falls back
// to the Brand Command mark — see LOGO_NOTICE / backlog 330.
async function resolveLogo(logoUrl) {
  if (!logoUrl) return null;
  try { return await fetchAsDataUrl(logoUrl); } catch { return null; }
}

const stripDataUrl = (s) => String(s).replace(/^data:[^;]+;base64,/, "");

// ── Chart image (SVG → PNG) ──────────────────────────────────────────────────

function niceNum(x) {
  if (x <= 0) return 1;
  const exp = Math.floor(Math.log10(x));
  const f = x / Math.pow(10, exp);
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nf * Math.pow(10, exp);
}

const kfmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v));

function buildChartSvg({ labels, series, title, type = "line" }, width = 920, height = 470) {
  const left = 62, right = 220, top = 40, bottom = 52;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const n = labels.length;
  const isBar = type === "bar";
  // backlog 323 — thin strokes need the 3:1 palette, large fills do not.
  const palette = isBar ? CHART_COLORS : LINE_COLORS;

  // Lines scale to the largest single series value; stacked bars scale to the
  // largest per-slot column total.
  const rawMax = isBar
    ? labels.reduce((mx, _, i) => Math.max(mx, series.reduce((s, ser) => s + (ser.values[i] || 0), 0)), 0)
    : series.reduce((mx, s) => Math.max(mx, ...s.values), 0);
  const step = niceNum((rawMax || 1) / 5);
  const niceMax = Math.max(step * 5, Math.ceil((rawMax || 1) / step) * step);

  const xAt = (i) => (n <= 1 ? left + plotW / 2 : left + (i * plotW) / (n - 1));
  const yAt = (v) => top + plotH - (v / niceMax) * plotH;

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
  svg += `<rect x="0" y="0" width="${width}" height="${height}" fill="#FFFFFF"/>`;
  if (title) svg += `<text x="${left}" y="24" font-family="Inter,Segoe UI,sans-serif" font-size="16" font-weight="800" fill="#1B2A4A">${esc(title)}</text>`;

  // Gridlines + y labels
  for (let t = 0; t <= 5; t++) {
    const v = (niceMax / 5) * t;
    const y = yAt(v);
    svg += `<line x1="${left}" y1="${y.toFixed(1)}" x2="${left + plotW}" y2="${y.toFixed(1)}" stroke="#E2E8F0" stroke-width="1"/>`;
    svg += `<text x="${left - 8}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-family="Inter,Segoe UI,sans-serif" font-size="11" fill="#6B7A99">${esc(kfmt(v))}</text>`;
  }
  // Axis "Cases"
  svg += `<text transform="translate(16 ${top + plotH / 2}) rotate(-90)" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="11" font-weight="600" fill="#6B7A99" letter-spacing="0.05em">CASES</text>`;

  // X labels (thin out when crowded)
  const showEvery = n > 14 ? 2 : 1;
  labels.forEach((lab, i) => {
    if (i % showEvery !== 0 && i !== n - 1) return;
    svg += `<text x="${xAt(i).toFixed(1)}" y="${height - bottom + 18}" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="10" fill="#6B7A99">${esc(lab)}</text>`;
  });
  // Baseline
  svg += `<line x1="${left}" y1="${top + plotH}" x2="${left + plotW}" y2="${top + plotH}" stroke="#CBD5E1" stroke-width="1"/>`;

  if (isBar) {
    // Stacked bars (mirrors the app's "Stacked Bar" view).
    const slotW = n > 1 ? plotW / (n - 1) : plotW;
    const bw = Math.max(6, Math.min(30, slotW * 0.6));
    const yB = top + plotH;
    labels.forEach((_, i) => {
      let acc = 0;
      series.forEach((s, si) => {
        const v = s.values[i] || 0;
        if (v <= 0) return;
        const color = palette[si % palette.length];
        const h = (v / niceMax) * plotH;
        const yTop = yB - ((acc + v) / niceMax) * plotH;
        svg += `<rect x="${(xAt(i) - bw / 2).toFixed(1)}" y="${yTop.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" fill="${color}"/>`;
        acc += v;
      });
    });
  } else {
    // Series lines + dots
    series.forEach((s, si) => {
      const color = palette[si % palette.length];
      const pts = s.values.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(" ");
      svg += `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`;
      s.values.forEach((v, i) => {
        svg += `<circle cx="${xAt(i).toFixed(1)}" cy="${yAt(v).toFixed(1)}" r="2.6" fill="${color}"/>`;
      });
    });
  }

  // Legend (right column)
  const lx = left + plotW + 18;
  let ly = top + 2;
  const maxRows = Math.floor(plotH / 18);
  // The legend swatch is what ties a name to a line, so it takes the SAME
  // colour the mark does — a 12px chip in the fill orange next to a line in the
  // 3:1 orange would just move the failure into the legend (backlog 323).
  series.slice(0, maxRows).forEach((s, si) => {
    const color = palette[si % palette.length];
    const name = s.name.length > 26 ? s.name.slice(0, 25) + "…" : s.name;
    svg += `<rect x="${lx}" y="${ly - 8}" width="12" height="12" rx="2" fill="${color}"/>`;
    svg += `<text x="${lx + 18}" y="${ly + 2}" font-family="Inter,Segoe UI,sans-serif" font-size="11" fill="#374151">${esc(name)}</text>`;
    ly += 18;
  });
  if (series.length > maxRows) {
    svg += `<text x="${lx}" y="${ly + 2}" font-family="Inter,Segoe UI,sans-serif" font-size="11" font-style="italic" fill="#6B7A99">+${series.length - maxRows} more…</text>`;
  }

  svg += `</svg>`;
  return svg;
}

const GROUP_BY_LABEL = {
  sku: "Product SKU", region: "Region", state: "State", distributor: "Distributor",
  size: "Bottle Size", packSize: "Pack Size", tag: "TAG", flavor: "Flavor",
};

// ── Main export ──────────────────────────────────────────────────────────────

export async function exportGridToExcel({
  rawForecast,
  products = [],
  skuNames = [],
  selectedYears = [],
  selectedRegion = "",
  selectedState = "",
  selectedDistributor = "",
  selectedSize = "",
  selectedPackSize = "",
  selectedTag = "",
  selectedFlavor = "",
  selectedSku = "",
  actorName = "",
  includeChart = false,
  chartType = "line",
  chartGroupBy = "sku",
}) {
  if (!selectedYears.length) throw new Error("Select a Year to export.");

  const ExcelJS = (await import("exceljs")).default;

  // SKU → attribute maps (same source as the dashboard).
  const skuToSize = {};
  const skuToPackSize = {};
  const skuToFlavor = {};
  const skuToTag = {};
  products.forEach((p) => {
    skuToSize[p.name] = p.sizes?.name ?? "";
    skuToPackSize[p.name] = p.pack_sizes?.name ?? "";
    skuToFlavor[p.name] = p.flavors?.name ?? "";
    skuToTag[p.name] = Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : [];
  });

  const view = buildForecastView({
    rawForecast, years: selectedYears,
    selectedRegion, selectedState, selectedDistributor, selectedSku,
    skuNames, skuToSize, skuToPackSize,
  });

  const years = view.years;
  const multiYear = years.length > 1;

  // Company branding (name + logo). Best-effort; never blocks export.
  let teamName = "";
  let logoUrl = null;
  let acctFailed = false;
  try {
    const acct = await getMyPrimaryAccount();
    teamName = acct?.name ?? "";
    logoUrl = acct?.logoUrl ?? null;
  } catch (e) { acctFailed = true; /* unbranded */ }
  const logoData = await resolveLogo(logoUrl);

  // backlog 214/335 — years the user asked for that hold no forecast records at
  // all. The grid labels these; the workbook used to write a full Jan–Dec block
  // of blanks under a grand total that covered the other year only, which is
  // the same "we forecast zero" vs "there is no forecast" lie in the artifact
  // the customer actually emails out.
  const emptyYears = years.filter((y) => !view.yearHasData?.[y]);
  const allYearsEmpty = years.length > 0 && emptyYears.length === years.length;

  // Column model (mirrors the grid).
  const columns = [];
  years.forEach((y) => {
    MONTHS.forEach((m, mi) => columns.push({ kind: "month", year: y, month: m, label: MONTH_ABBR[mi] }));
    columns.push({ kind: "total", year: y, label: "Total" });
  });
  if (multiYear) columns.push({ kind: "grand", label: "All Yrs" });
  const colCount = 1 + columns.length;

  const filters = [
    ["Region", selectedRegion],
    ["State", selectedState],
    ["Distributor", selectedDistributor],
    ["Bottle Size", selectedSize],
    ["Pack Size", selectedPackSize],
    ["TAG", selectedTag],
    ["Flavor", selectedFlavor],
    ["Product SKU", selectedSku],
  ].filter(([, v]) => v);

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  // ── Workbook ──
  const wb = new ExcelJS.Workbook();
  wb.creator = "Brand Command";
  wb.created = now;
  const ws = wb.addWorksheet("Sales Forecast", { views: [{ showGridLines: false }] });

  // Column widths
  ws.getColumn(1).width = 30;
  columns.forEach((c, i) => { ws.getColumn(i + 2).width = c.kind === "month" ? 9.5 : c.kind === "total" ? 12 : 13; });

  const mergeRow = (r, text, style = {}) => {
    ws.mergeCells(r, 1, r, colCount);
    const cell = ws.getCell(r, 1);
    cell.value = text;
    cell.font = style.font || {};
    if (style.fill) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: style.fill } };
    cell.alignment = style.alignment || { vertical: "middle", horizontal: "left" };
    if (style.height) ws.getRow(r).height = style.height;
    return cell;
  };

  // Logo (floated over the top-left) in a tall row 1 — but ONLY when there is a
  // tenant logo to place, else the sheet starts at the title instead of leaving
  // a blank band where ours used to sit (backlog 330).
  let logoStatus = logoUrl || acctFailed ? "failed" : "none";
  let r = 1;
  if (logoData) {
    try {
      const png = await rasterizeToPng(logoData, 230, 48);
      if (png) {
        ws.getRow(1).height = 46;
        const imgId = wb.addImage({ base64: png.base64, extension: "png" });
        ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: png.width, height: png.height } });
        logoStatus = "ok";
        r = 2;
      }
    } catch (e) { /* skip image on failure */ }
  }

  mergeRow(r++, "Sales Forecast", { font: { name: "Inter", size: 16, bold: true, color: { argb: C.navy } } });
  // Bold, prominent forecast year(s) — an empty year is called out by name here
  // as well as in its column header (backlog 335).
  const yearLabel = years.map((y) => (view.yearHasData?.[y] ? String(y) : `${y} (no forecast)`)).join(", ");
  mergeRow(r++, `Forecast Year${multiYear ? "s" : ""}: ${yearLabel}`, { font: { name: "Inter", size: 13, bold: true, color: { argb: C.accent } } });
  if (emptyYears.length) {
    mergeRow(r++, allYearsEmpty
      ? `⚠ No forecast records exist for ${emptyYears.join(", ")} — every figure below is blank, not zero.`
      : `⚠ No forecast records exist for ${emptyYears.join(", ")} — those month columns and totals are blank, not zero, and the All Yrs column covers ${years.filter((y) => view.yearHasData?.[y]).join(", ")} only.`,
      { font: { name: "Inter", size: 10, bold: true, color: { argb: C.accent } } });
  }
  if (teamName) mergeRow(r++, teamName, { font: { name: "Inter", size: 12, bold: true, color: { argb: C.navy } } });
  // accent divider
  mergeRow(r++, "", { fill: C.accentBar, height: 4 });
  mergeRow(r++, `Exported: ${dateStr} at ${timeStr}     ·     Exported by: ${actorName || "—"}`, { font: { name: "Inter", size: 10, color: { argb: C.muted } } });
  if (filters.length) {
    mergeRow(r++, "Filters — " + filters.map(([k, v]) => `${k}: ${v}`).join("   ·   "), { font: { name: "Inter", size: 10, italic: true, color: { argb: C.muted } } });
  }
  ws.getRow(r++).height = 6; // spacer

  // ── Table header ──
  const headBase = { font: { name: "Inter", size: 11, bold: true, color: { argb: C.white } } };
  const headFill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navy } };

  let headerLastRow;
  if (multiYear) {
    const superR = r;
    const subR = r + 1;
    headerLastRow = subR;
    // SKU spans both header rows
    ws.mergeCells(superR, 1, subR, 1);
    let cell = ws.getCell(superR, 1);
    cell.value = "Product SKU";
    cell.font = headBase.font; cell.fill = headFill;
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = ALL_BORDERS;

    // Year groups (each spans its 12 months + Total) — BOLD years
    let c0 = 2;
    years.forEach((y) => {
      ws.mergeCells(superR, c0, superR, c0 + 12);
      const yc = ws.getCell(superR, c0);
      // backlog 335 — mirror the grid: an empty year says so in its own header,
      // so a block of blank cells can't be read as a forecast of zero.
      const yearEmpty = !view.yearHasData?.[y];
      yc.value = yearEmpty ? `${y} — no forecast` : Number(y);
      yc.font = { name: "Inter", size: 12, bold: true, color: { argb: C.white } };
      yc.fill = headFill;
      yc.alignment = { vertical: "middle", horizontal: "center" };
      yc.border = ALL_BORDERS;
      c0 += 13;
    });
    // All Yrs spans both rows
    ws.mergeCells(superR, c0, subR, c0);
    const ac = ws.getCell(superR, c0);
    ac.value = "All Yrs";
    ac.font = headBase.font; ac.fill = headFill;
    ac.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    ac.border = ALL_BORDERS;

    // Sub header: month/total labels
    let cc = 2;
    columns.forEach((col) => {
      if (col.kind === "grand") return; // covered by merged All Yrs
      const hc = ws.getCell(subR, cc);
      hc.value = col.label;
      hc.font = col.kind === "total" ? { name: "Inter", size: 11, bold: true, color: { argb: C.white } } : headBase.font;
      hc.fill = headFill;
      hc.alignment = { vertical: "middle", horizontal: "center" };
      hc.border = ALL_BORDERS;
      cc++;
    });
    r = subR + 1;
  } else {
    headerLastRow = r;
    const skuC = ws.getCell(r, 1);
    skuC.value = "Product SKU";
    skuC.font = headBase.font; skuC.fill = headFill;
    skuC.alignment = { vertical: "middle", horizontal: "left" };
    skuC.border = ALL_BORDERS;
    columns.forEach((col, i) => {
      const hc = ws.getCell(r, i + 2);
      hc.value = col.kind === "total" ? `Total ${col.year}` : col.label;
      hc.font = headBase.font; hc.fill = headFill;
      hc.alignment = { vertical: "middle", horizontal: "center" };
      hc.border = ALL_BORDERS;
    });
    r++;
  }

  // Freeze the SKU column + header rows
  ws.views = [{ state: "frozen", xSplit: 1, ySplit: headerLastRow, showGridLines: false }];

  // ── Body: groups + SKU rows ──
  const numFmt = "#,##0";
  const writeValueCell = (row, colNo, v, { bold = false, bg = null } = {}) => {
    const cell = ws.getCell(row, colNo);
    cell.value = v > 0 ? v : null;
    cell.numFmt = numFmt;
    cell.font = { name: "Inter", size: 10, bold, color: { argb: C.text } };
    cell.alignment = { horizontal: "right" };
    if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell.border = ALL_BORDERS;
    return cell;
  };

  view.groups.forEach((g) => {
    // Group sub-heading (merged)
    ws.mergeCells(r, 1, r, colCount);
    const gc = ws.getCell(r, 1);
    gc.value = `${g.label}   (${g.rows.length} SKU${g.rows.length === 1 ? "" : "s"})`;
    gc.font = { name: "Inter", size: 11, bold: true, color: { argb: C.navy } };
    gc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
    gc.alignment = { vertical: "middle", horizontal: "left" };
    gc.border = ALL_BORDERS;
    r++;

    g.rows.forEach((row, i) => {
      const altBg = i % 2 ? null : C.altRow;
      const sc = ws.getCell(r, 1);
      sc.value = row.sku;
      sc.font = { name: "Inter", size: 10, bold: true, color: { argb: C.text } };
      sc.alignment = { horizontal: "left" };
      if (altBg) sc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: altBg } };
      sc.border = ALL_BORDERS;
      columns.forEach((col, ci) => {
        let v = 0;
        if (col.kind === "month") v = row.cells[col.year]?.[col.month]?.value ?? 0;
        else if (col.kind === "total") v = row.yearTotals[col.year] ?? 0;
        else v = row.total;
        const isTot = col.kind !== "month";
        writeValueCell(r, ci + 2, v, { bold: isTot, bg: isTot ? C.totalBg : altBg });
      });
      r++;
    });
  });

  if (!view.groups.length) {
    mergeRow(r++, "No forecast data for the selected filters.", { font: { name: "Inter", size: 11, color: { argb: C.muted } }, alignment: { horizontal: "center" } });
  }

  // ── Subtotals by Bottle Size / Pack Size (bottom block, accent-fenced) ──
  if (view.groups.length) {
    mergeRow(r++, "", { fill: C.accentBar, height: 3 });
    mergeRow(r++, "Subtotals by Bottle Size / Pack Size", { fill: C.groupBg, font: { name: "Inter", size: 10, bold: true, color: { argb: C.accent } } });
    view.groups.forEach((g) => {
      const sc = ws.getCell(r, 1);
      sc.value = g.label;
      sc.font = { name: "Inter", size: 10, bold: true, color: { argb: C.navy } };
      sc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.subBg } };
      sc.alignment = { horizontal: "left" };
      sc.border = ALL_BORDERS;
      columns.forEach((col, ci) => {
        let v = 0;
        if (col.kind === "month") v = g.groupYM[col.year]?.[col.month] ?? 0;
        else if (col.kind === "total") v = g.groupYearTotals[col.year] ?? 0;
        else v = g.groupTotal;
        writeValueCell(r, ci + 2, v, { bold: true, bg: C.subBg });
      });
      r++;
    });
    mergeRow(r++, "", { fill: C.accentBar, height: 3 });
  }

  // ── Grand total ──
  {
    const lc = ws.getCell(r, 1);
    lc.value = "Total (All SKUs)";
    lc.font = { name: "Inter", size: 11, bold: true, color: { argb: C.white } };
    lc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navy } };
    lc.alignment = { horizontal: "left" };
    lc.border = ALL_BORDERS;
    columns.forEach((col, ci) => {
      let v = 0;
      if (col.kind === "month") v = view.colYM[col.year]?.[col.month] ?? 0;
      else if (col.kind === "total") v = view.colYearTotals[col.year] ?? 0;
      else v = view.grandTotal;
      const cell = ws.getCell(r, ci + 2);
      cell.value = v > 0 ? v : null;
      cell.numFmt = numFmt;
      cell.font = { name: "Inter", size: 11, bold: true, color: { argb: C.white } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navy } };
      cell.alignment = { horizontal: "right" };
      cell.border = ALL_BORDERS;
    });
    r++;
  }
  mergeRow(r++, "Forecast in physical cases. Generated by Brand Command — Sales Forecasting.", { font: { name: "Inter", size: 9, italic: true, color: { argb: C.muted } } });

  // ── Optional chart worksheet ──
  if (includeChart) {
    const { labels, series } = buildChartSeries({
      rawForecast, years: selectedYears,
      selectedRegion, selectedState, selectedDistributor, selectedSku,
      groupBy: chartGroupBy, skuToSize, skuToPackSize, skuToFlavor, skuToTag,
    });
    const cs = wb.addWorksheet("Forecast Chart", { views: [{ showGridLines: false }] });
    cs.getColumn(1).width = 4;
    const titleText = `Forecast by ${GROUP_BY_LABEL[chartGroupBy] || "Product SKU"} — ${years.join(", ")}`;
    if (series.length) {
      try {
        const svg = buildChartSvg({ labels, series, title: titleText, type: chartType });
        const png = await svgToPngDataUrl(svg, 920, 470);
        const chartId = wb.addImage({ base64: stripDataUrl(png), extension: "png" });
        cs.addImage(chartId, { tl: { col: 1, row: 1 }, ext: { width: 920, height: 470 } });
      } catch (e) {
        cs.getCell(2, 2).value = "Chart could not be generated.";
      }
    } else {
      cs.getCell(2, 2).value = `${titleText}: no data for the selected filters.`;
    }
  }

  // ── Download ──
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const team = (teamName || "team").trim() || "team";
  const yearPart = years.join("-");
  const filename = `${team}_sales_forecast_${yearPart}.xlsx`.replace(/[^A-Za-z0-9._-]+/g, "_");

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  // `logo` lets the caller warn in the export dialog when the workbook went out
  // with no mark on it (backlog 330).
  return { logo: logoStatus };
}
